/**
 * Pack Products - Gestion de la sélection multiple de produits avec ajout au panier groupé
 *
 * Fonctionnalités :
 * - Sélection/désélection de produits via checkbox
 * - Changement de variantes avec mise à jour des prix
 * - Calcul du total en temps réel
 * - Application d'un code promo bundle (BUNDLE15/30/40/60) selon le palier atteint
 * - Ajout au panier groupé via Cart API
 * - Animations similaires au système standard d'ajout au panier
 * - Accessibilité complète (ARIA, screen readers)
 */

import { CartAddEvent, ThemeEvents } from "@theme/events";

class PackProducts {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = this.section.dataset.sectionId;
    this.moneyFormat = this.section.dataset.moneyFormat || "{{amount}}€";
    this.tierType = this.section.dataset.tierType || "product_count"; // "amount" ou "product_count"
    this.discountUnit = this.section.dataset.discountUnit || "amount"; // "amount" (€) ou "percentage" (%)
    this.bundleMode =
      this.section.dataset.bundleMode || "standard_tier"; // "standard_tier" | "two_plus_one_free"
    this.freeGiftDiscountCode =
      this.section.dataset.freeGiftDiscountCode || "";

    // État de la sélection : Map {productKey -> {checked, selectedVariantId, price}}
    this.selectionState = new Map();
    this.freeGiftVariantId = null; // Pour le mode 2+1

    // Éléments DOM
    this.checkboxes = this.section.querySelectorAll(".pack-products__checkbox");
    this.variantSelects = this.section.querySelectorAll(
      ".pack-products__variant-select"
    );
    this.addToCartButton = this.section.querySelector(
      ".pack-products__add-to-cart"
    );
    this.totalDisplay = this.section.querySelector(".pack-products__total");
    this.statusDisplay = this.section.querySelector(
      `#pack-status-${this.sectionId}`
    );

    // Charger la configuration des paliers depuis les data-attributes
    this.tiersConfig = this.loadTiersConfig();

    // La barre de progression et le résumé sont maintenant dans la sidebar (non sticky)
    this.sidebar = this.section.querySelector(".pack-products__sidebar");
    this.progressContainer = this.sidebar
      ? this.sidebar.querySelector(".pack-products__progress-container")
      : null;
    this.progressBars = this.progressContainer
      ? this.progressContainer.querySelector(".pack-products__progress-bars")
      : null;
    this.progressAmount = this.progressContainer
      ? this.progressContainer.querySelector(".pack-products__progress-amount")
      : null;

    // Éléments du résumé dans la sidebar
    this.summaryItems = this.sidebar
      ? this.sidebar.querySelector(".pack-products__summary-items")
      : null;
    this.summaryTotal = this.sidebar
      ? this.sidebar.querySelector(".pack-products__summary-total-amount")
      : null;
    this.summarySavings = this.sidebar
      ? this.sidebar.querySelector(`#pack-summary-savings-${this.sectionId}`)
      : null;
    this.summaryCount = this.sidebar
      ? this.sidebar.querySelector(".pack-products__summary-count")
      : null;

    // Mode 2+1 : sélecteur de cadeau offert (cartes)
    this.freeGiftSelector = this.section.querySelector(
      `#pack-free-gift-${this.sectionId}`
    );
    this.freeGiftDetails = this.section.querySelector(
      `#pack-free-gift-details-${this.sectionId}`
    );
    this.freeGiftCards = this.section.querySelector(
      `#pack-free-gift-cards-${this.sectionId}`
    );
    this.freeGiftConfig = this.loadFreeGiftConfig();

    // URLs Cart API (cached for performance)
    this.cartAddUrl =
      (typeof Theme !== "undefined" && Theme.routes?.cart_add_url) ||
      (typeof window.Shopify !== "undefined" && window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart/add.js`
        : "/cart/add.js");
    this.cartUpdateUrl =
      (typeof Theme !== "undefined" && Theme.routes?.cart_update_url) ||
      "/cart/update.js";

    // Debug: vérifier que les éléments sont trouvés
    if (!this.progressContainer) {
      console.warn(
        `PackProducts: progressContainer not found for section ${this.sectionId}`
      );
    }
    if (!this.progressBars) {
      console.warn(
        `PackProducts: progressBars not found for section ${this.sectionId}`
      );
    }

    // Données des produits
    this.productsData = this.loadProductsData();

    // Timeouts pour les animations
    this.animationTimeout = null;
    this.cleanupTimeout = null;

    this.init();
  }

  /**
   * Charge les données des produits depuis les scripts JSON
   */
  loadProductsData() {
    const data = {};
    const scripts = this.section.querySelectorAll("script[data-product-data]");

    scripts.forEach((script) => {
      const productKey = script.dataset.productData;
      try {
        data[productKey] = JSON.parse(script.textContent);
      } catch (error) {
        console.error(
          `Erreur lors du parsing des données pour ${productKey}:`,
          error
        );
      }
    });

    return data;
  }

  /**
   * Charge la configuration du cadeau offert (mode 2+1)
   */
  loadFreeGiftConfig() {
    if (this.bundleMode !== "two_plus_one_free") return [];
    const script = this.section.querySelector(
      `script[data-free-gift-config]`
    );
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn("[Pack Products] Erreur parsing free gift config:", e);
      return [];
    }
  }

  /**
   * Initialise les événements et l'état initial
   */
  init() {
    // Initialiser l'état pour chaque produit
    // Les variantes sont déjà filtrées (ou non) par Liquid selon filter_variants
    Object.keys(this.productsData).forEach((productKey) => {
      const product = this.productsData[productKey];
      const allowedVariants = product.variants || [];

      // Prioriser 10g, puis 5g si filtrage actif, sinon première disponible
      const variant10g = allowedVariants.find((v) => {
        const title = (v.title || "").toLowerCase();
        return (
          title.includes("10g") ||
          title.includes("10 g") ||
          title.includes("(10g)") ||
          title.includes("(10 g)")
        );
      });
      const variant5g = allowedVariants.find((v) => {
        const title = (v.title || "").toLowerCase();
        return (
          title.includes("5g") ||
          title.includes("5 g") ||
          title.includes("(5g)") ||
          title.includes("(5 g)")
        );
      });
      const firstAvailableVariant =
        (variant10g && variant10g.available ? variant10g : null) ||
        (variant5g && variant5g.available ? variant5g : null) ||
        allowedVariants.find((v) => v.available) ||
        allowedVariants[0];

      this.selectionState.set(productKey, {
        checked: false,
        selectedVariantId: firstAvailableVariant
          ? firstAvailableVariant.id
          : null,
        price: firstAvailableVariant
          ? firstAvailableVariant.price
          : product.price_min || 0,
        compareAtPrice: firstAvailableVariant
          ? firstAvailableVariant.compare_at_price || 0
          : 0,
      });

      // Mettre à jour l'affichage du prix pour la variante sélectionnée par défaut
      if (firstAvailableVariant) {
        this.updatePriceDisplay(
          productKey,
          firstAvailableVariant.price,
          firstAvailableVariant.compare_at_price || 0
        );
      }
    });

    // Écouteurs d'événements
    this.bindEvents();

    // Mise à jour initiale
    this.updateDisplay();

    // Synchroniser les selects avec la variante sélectionnée par défaut (10g prioritaire)
    this.syncVariantSelects();

    // Mode 2+1 : peupler le sélecteur de cadeau offert
    this.populateFreeGiftSelect();

    // Écouter les changements du panier pour vérifier les produits bundle
    this.setupCartChangeListener();
  }

  /**
   * Remplit le sélecteur de cadeau offert avec des cartes (image + titre + grammage)
   */
  populateFreeGiftSelect() {
    if (
      this.bundleMode !== "two_plus_one_free" ||
      !this.freeGiftCards ||
      !this.freeGiftConfig.length
    )
      return;
    this.freeGiftCards.innerHTML = "";
    this.freeGiftConfig.forEach((opt) => {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "pack-products__free-gift-card";
      card.dataset.variantId = String(opt.id);
      card.setAttribute("role", "option");
      card.setAttribute("aria-selected", "false");

      const imgWrap = document.createElement("span");
      imgWrap.className = "pack-products__free-gift-card-image";
      if (opt.image_url) {
        const img = document.createElement("img");
        img.src = opt.image_url;
        img.alt = "";
        img.loading = "lazy";
        imgWrap.appendChild(img);
      }

      const titleSpan = document.createElement("span");
      titleSpan.className = "pack-products__free-gift-card-title";
      const t = (opt.title || "").substring(0, 35);
      titleSpan.textContent = t + ((opt.title || "").length > 35 ? "…" : "");

      const variantTitle = (opt.variant_title || "").trim();
      const hasVariant =
        variantTitle && variantTitle.toLowerCase() !== "default title";

      const variantSpan = document.createElement("span");
      variantSpan.className = "pack-products__free-gift-card-variant";
      variantSpan.textContent = hasVariant ? variantTitle : "";

      card.appendChild(imgWrap);
      card.appendChild(titleSpan);
      if (hasVariant) card.appendChild(variantSpan);

      if (opt.available === false) {
        const soldout = document.createElement("span");
        soldout.className = "pack-products__free-gift-card-soldout";
        soldout.textContent = "Épuisé";
        card.appendChild(soldout);
        card.classList.add("pack-products__free-gift-card--soldout");
        card.disabled = true;
      } else {
        card.addEventListener("click", () => this.handleFreeGiftCardClick(opt.id));
      }
      this.freeGiftCards.appendChild(card);
    });
  }

  /**
   * Gère le clic sur une carte de cadeau offert
   */
  handleFreeGiftCardClick(variantId) {
    if (this.freeGiftVariantId === String(variantId)) {
      this.freeGiftVariantId = null;
    } else {
      this.freeGiftVariantId = String(variantId);
      // Fermer le details quand un cadeau est choisi
      if (this.freeGiftDetails) {
        this.freeGiftDetails.removeAttribute("open");
      }
    }
    this.updateFreeGiftCardsSelection();
    this.updateDisplay();
  }

  /**
   * Met à jour l'état sélectionné des cartes cadeau
   */
  updateFreeGiftCardsSelection() {
    if (!this.freeGiftCards) return;
    this.freeGiftCards.querySelectorAll(".pack-products__free-gift-card").forEach((card) => {
      const isSelected = card.dataset.variantId === this.freeGiftVariantId;
      card.classList.toggle("pack-products__free-gift-card--selected", isSelected);
      card.setAttribute("aria-selected", String(isSelected));
    });
  }

  /**
   * Synchronise les selects de variantes avec la variante sélectionnée par défaut
   */
  syncVariantSelects() {
    this.variantSelects.forEach((select) => {
      const productKey = select.dataset.productKey;
      const state = this.selectionState.get(productKey);
      if (state && state.selectedVariantId) {
        // Trouver l'option correspondant à la variante sélectionnée
        const options = Array.from(select.options);
        const matchingOption = options.find(
          (option) => option.value === state.selectedVariantId.toString()
        );
        if (matchingOption) {
          select.selectedIndex = options.indexOf(matchingOption);
          // On iOS, programmatic change might not fire events properly
          // So we call the handler directly AND dispatch events
          this.handleVariantChange(select);
          // Also dispatch events for any other listeners
          select.dispatchEvent(new Event("change", { bubbles: true }));
          select.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    });
  }

  /**
   * Configure les écouteurs pour les changements du panier
   * Vérifie automatiquement si le code bundle doit être retiré
   */
  setupCartChangeListener() {
    // Dans ce thème, toutes les mises à jour du panier dispatchent ThemeEvents.cartUpdate
    // (via CartAddEvent ou CartUpdateEvent). On s'y accroche pour vérifier le bundle.
    document.addEventListener(ThemeEvents.cartUpdate, async () => {
      const hasMinimum = await this.verifyMinimumBundleItems(2);
      if (!hasMinimum) {
        await this.removeBundleDiscount();
      }
    });
  }

    /**
     * Attache les écouteurs d'événements
     */
    bindEvents() {
    // Simplified touch handling for mobile - prevent double-firing
    let lastTouchTime = 0;

    this.section.addEventListener(
      "touchend",
      (e) => {
        // Only handle if it's a card click, not if it's a select or other interactive element
        const card = e.target.closest(".pack-products__card");
        if (
          card &&
          card.dataset.soldOut !== "true" &&
          !this.isInteractive(e.target) &&
          !e.target.closest(".pack-products__variant-select") &&
          !e.target.closest("a") &&
          !e.target.closest("button")
        ) {
          const now = Date.now();
          // Prevent double-firing within 300ms (iOS click delay)
          if (now - lastTouchTime > 300) {
            lastTouchTime = now;
            e.preventDefault();
            this.handleCardClick(e);
          }
        }
      },
      { passive: false }
    );

      // Délégation d'événements pour les cartes cliquables
    this.section.addEventListener("click", (e) => {
      // On mobile, click fires ~300ms after touchend, so skip if recent touch
      const now = Date.now();
      if (now - lastTouchTime < 350) {
        return;
      }
        this.handleCardClick(e);
      });

    this.section.addEventListener("keydown", (e) => {
        this.handleCardKeydown(e);
      });

      // Checkboxes de sélection (pour compatibilité)
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
          this.handleCheckboxChange(e.target);
        });
      });

    // Sélecteurs de variantes - add both change and input events for better mobile support
    this.variantSelects.forEach((select) => {
      select.addEventListener("change", (e) => {
        this.handleVariantChange(e.target);
      });
      select.addEventListener("input", (e) => {
        this.handleVariantChange(e.target);
      });
    });

    // Mode 2+1 : sélecteur de cadeau offert (cartes cliquables)

    // Bouton d'ajout au panier
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener("click", () => {
        this.handleAddToCart();
      });
    }
  }

    /**
     * Vérifie si un élément est interactif
     */
    isInteractive(element) {
    const interactiveTags = ["SELECT", "A", "BUTTON", "INPUT", "TEXTAREA"];
      const path = element.composedPath ? element.composedPath() : [element];

    return path.some((el) => {
        if (!el.tagName) return false;
      return (
        interactiveTags.includes(el.tagName) ||
        el.hasAttribute("contenteditable") ||
        (el.hasAttribute("tabindex") &&
          el.getAttribute("tabindex") !== "-1" &&
          el.getAttribute("role") !== "checkbox")
      );
      });
    }

    /**
     * Gère le clic sur une carte
     */
    handleCardClick(event) {
      // Ignorer si le clic vient d'un élément interactif
      if (this.isInteractive(event.target)) {
        return;
      }

    // Ignorer les clics sur les liens, boutons, et selects
    if (
      event.target.closest("a") ||
      event.target.closest("button") ||
      event.target.closest(".pack-products__variant-select")
    ) {
      return;
    }

      const card = event.target.closest('.pack-products__card[role="checkbox"]');
      if (card && card.dataset.soldOut !== "true") {
        event.preventDefault();
      event.stopPropagation();
        this.toggleCardSelection(card);
      }
    }

    /**
     * Gère les événements clavier sur une carte
     */
    handleCardKeydown(event) {
    if (event.key === " " || event.key === "Enter") {
      const card = event.target.closest(
        '.pack-products__card[role="checkbox"]'
      );
        if (card && card.dataset.soldOut !== "true" && event.target === card) {
          event.preventDefault();
          this.toggleCardSelection(card);
        }
      }
    }

  /**
   * Toggle la sélection d'une carte
   */
  toggleCardSelection(card) {
    if (card.dataset.soldOut === "true") return;
    const productKey = card.dataset.productKey;
    const state = this.selectionState.get(productKey);

    if (state) {
      const newCheckedState = !state.checked;
      // Mode 2+1 : max 2 produits sélectionnés
      if (newCheckedState && this.bundleMode === "two_plus_one_free") {
        const selectedCount = this.getSelectedCount();
        if (selectedCount >= 2) return;
      }
      state.checked = newCheckedState;
        this.selectionState.set(productKey, state);

        // Mise à jour de la carte et de la checkbox cachée
        this.updateCardSelection(productKey, newCheckedState);

        // Mise à jour du total
        this.updateDisplay();

        // Annonce pour les screen readers
        this.announceSelection(productKey, newCheckedState);
      }
    }

  /**
   * Gère le changement d'état d'une checkbox (pour compatibilité)
   */
  handleCheckboxChange(checkbox) {
    const productKey = checkbox.dataset.productKey;
    const state = this.selectionState.get(productKey);

    if (state) {
      // Mode 2+1 : max 2 produits, ignorer si on dépasse
      if (
        checkbox.checked &&
        this.bundleMode === "two_plus_one_free" &&
        this.getSelectedCount() >= 2
      ) {
        checkbox.checked = false;
        return;
      }
      state.checked = checkbox.checked;
      this.selectionState.set(productKey, state);

      // Mise à jour visuelle de la carte
      this.updateCardSelection(productKey, checkbox.checked);

      // Mise à jour du total et du bouton
      this.updateDisplay();

      // Annonce pour les screen readers
      this.announceSelection(productKey, checkbox.checked);
    }
  }

  /**
   * Gère le changement de variante
   */
  handleVariantChange(select) {
    const productKey = select.dataset.productKey;
    if (!productKey) {
      console.error("Variant select missing data-product-key attribute");
      return;
    }

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) {
      console.error("No option selected in variant select");
      return;
    }

    const variantId = selectedOption.value;
    const price = parseInt(selectedOption.dataset.price);
    const compareAtPrice = parseInt(selectedOption.dataset.compareAtPrice) || 0;

    const state = this.selectionState.get(productKey);
    if (state) {
      state.selectedVariantId = variantId;
      state.price = price;
      state.compareAtPrice = compareAtPrice;
      this.selectionState.set(productKey, state);

      // Mise à jour de l'affichage du prix
      this.updatePriceDisplay(productKey, price, compareAtPrice);

      // Si la carte est sélectionnée, recalculer le total
      if (state.checked) {
        this.updateDisplay();
      }
    } else {
      console.error(`No selection state found for product key: ${productKey}`);
    }
  }

  /**
   * Met à jour l'affichage visuel de la sélection d'une carte
   */
  updateCardSelection(productKey, isSelected) {
    const card = this.section.querySelector(
      `[data-product-key="${productKey}"]`
    );
      if (card) {
        // Mise à jour de la classe CSS et des attributs ARIA
      card.classList.toggle("pack-products__card--selected", isSelected);
      card.setAttribute("aria-checked", isSelected.toString());

        // Synchronisation de la checkbox cachée
      const checkbox = card.querySelector(".pack-products__checkbox");
        if (checkbox) {
          checkbox.checked = isSelected;
        }
      }
    }

  /**
   * Met à jour l'affichage du prix d'un produit
   */
  updatePriceDisplay(productKey, price, compareAtPrice = 0) {
    const priceDisplay = this.section.querySelector(
      `[data-price-display="${productKey}"]`
    );
    if (priceDisplay) {
      const priceAmount = priceDisplay.querySelector(
        ".pack-products__price-amount"
      );
      const priceFrom = priceDisplay.querySelector(
        ".pack-products__price-from"
      );

      if (priceAmount) {
        priceAmount.textContent = this.formatMoney(price);
      }

      // Masquer "À partir de" quand une variante spécifique est sélectionnée
      if (priceFrom) {
        priceFrom.style.display = "none";
      }

      // Gérer le prix comparé (barré)
      let priceCompare = priceDisplay.querySelector(
        ".pack-products__price-compare"
      );

      if (compareAtPrice > price) {
        // Afficher ou créer le prix comparé
        if (!priceCompare) {
          priceCompare = document.createElement("span");
          priceCompare.className = "pack-products__price-compare";
          priceCompare.innerHTML = "<s></s>";
          // Insérer avant le prix principal
          priceDisplay.insertBefore(priceCompare, priceAmount);
        }
        priceCompare.querySelector("s").textContent =
          this.formatMoney(compareAtPrice);
        priceCompare.style.display = "";
      } else {
        // Masquer le prix comparé s'il n'y en a pas
        if (priceCompare) {
          priceCompare.style.display = "none";
        }
      }
    }

    // Gérer le badge de réduction - positionné sur la carte, pas dans le prix
    // Moved outside priceDisplay check to ensure it always runs
    const card = this.section.querySelector(
      `.pack-products__card[data-product-key="${productKey}"]`
    );

    if (card) {
      let discountBadge = card.querySelector(".pack-products__discount-badge");

      if (compareAtPrice > price) {
        // Calculer le pourcentage de réduction
        const discountPercentage = Math.round(
          ((compareAtPrice - price) / compareAtPrice) * 100
        );

        // Afficher ou créer le badge
        if (!discountBadge) {
          discountBadge = document.createElement("span");
          discountBadge.className = "pack-products__discount-badge";
          discountBadge.setAttribute("data-discount-badge", productKey);
          card.appendChild(discountBadge);
        }
        discountBadge.textContent = `-${discountPercentage}%`;
        discountBadge.style.display = "";
      } else {
        // Masquer le badge s'il n'y a pas de réduction
        if (discountBadge) {
          discountBadge.style.display = "none";
        }
      }
    }
  }

  /**
   * Met à jour le total et l'état du bouton
   */
  updateDisplay() {
    const { subtotal, discount, total } = this.calculateTotalWithDiscount();
    const selectedCount = this.getSelectedCount();
    const isTwoPlusOne = this.bundleMode === "two_plus_one_free";
    const canAddToCart = isTwoPlusOne
      ? selectedCount === 2 && !!this.freeGiftVariantId
      : selectedCount > 0;

    // Mise à jour du total
    if (this.totalDisplay) {
      if (discount > 0) {
        this.totalDisplay.innerHTML = `${this.formatMoney(
          total
        )} <span style="color: var(--color-foreground, #100f0d); font-weight: 800; font-size: 0.9em;">(-${this.formatMoney(
          discount
        )})</span>`;
      } else {
        this.totalDisplay.textContent = this.formatMoney(total);
      }
    }

    // Mode 2+1 : afficher/masquer le sélecteur de cadeau offert
    if (this.freeGiftSelector) {
      this.freeGiftSelector.style.display =
        isTwoPlusOne && selectedCount === 2 ? "block" : "none";
      if (isTwoPlusOne && selectedCount === 2) {
        this.updateFreeGiftCardsSelection();
        // Ouvrir le details à l'apparition (sauf si un cadeau est déjà choisi)
        if (this.freeGiftDetails) {
          if (!this.freeGiftVariantId) {
            this.freeGiftDetails.setAttribute("open", "");
          } else {
            this.freeGiftDetails.removeAttribute("open");
          }
        }
      }
    }

    // Barre de progression : masquée en mode 2+1
    if (isTwoPlusOne) {
      if (this.progressContainer)
        this.progressContainer.style.display = "none";
    } else {
      const progressValue =
        this.tierType === "amount" ? subtotal : selectedCount;
      this.updateProgressBar(progressValue, discount);
    }

    // Mise à jour de la sidebar
    this.updateSummary();

    // Boutons : désactivés si conditions non remplies
    if (this.addToCartButton) {
      this.addToCartButton.disabled = !canAddToCart;
      this.addToCartButton.classList.toggle(
        "pack-products__add-to-cart--disabled",
        !canAddToCart
      );
    }

    this.updateStatusMessage(selectedCount, total, discount);
  }

  /**
   * Crée une barre de progression pour un palier donné
   */
  createProgressBarElement(tier, progressValue, isReached, currentValue) {
    // Créer l'élément progress-group
    const progressGroup = document.createElement("div");
    progressGroup.className = "progress-group";

    // Créer l'élément progress
    const progressElement = document.createElement("progress");
    progressElement.className = "progress";
    progressElement.max = 100;
    progressElement.value = progressValue;

    // Créer le wrapper d'icône
    const iconWrapper = document.createElement("div");
    iconWrapper.className = "progress-icon-wrapper";

    // Créer l'icône
    const iconDiv = document.createElement("div");
    iconDiv.className = `progress-icon ${
      isReached ? "progress-icon--reached" : ""
    }`;

    // Icône (même SVG que icon-progress.liquid)
    iconDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
        <g filter="url(#filter0_d_706_205)">
          <path d="M9.38366 19.9999C9.4522 19.9999 9.51533 19.99 9.56042 19.9821C9.61814 19.9723 9.68307 19.9585 9.75341 19.9428C9.82736 19.9605 9.90492 19.9566 9.97887 19.9271C10.0005 19.9192 10.4713 19.7343 10.8194 19.5809C10.8536 19.5809 10.8897 19.579 10.924 19.5691C10.9727 19.5573 11.4254 19.4275 12.2064 18.8512C12.2695 18.8099 12.3308 18.7686 12.3922 18.7273C12.4463 18.7194 12.4986 18.6978 12.5473 18.6663C12.5581 18.6585 12.7547 18.5267 13 18.3143C13.2453 18.5247 13.4419 18.6585 13.4527 18.6663C13.5014 18.6998 13.5537 18.7194 13.6078 18.7273C13.6692 18.7686 13.7305 18.8099 13.7936 18.8512C14.5728 19.4295 15.0255 19.5573 15.076 19.5691C15.1121 19.579 15.1464 19.5829 15.1806 19.5809C15.5287 19.7343 15.9995 19.9192 16.0211 19.9271C16.0969 19.9566 16.1744 19.9605 16.2466 19.9428C16.3169 19.9605 16.3819 19.9723 16.4396 19.9821C16.5694 20.0038 16.8382 20.0471 17.0041 19.813C17.1538 19.6026 17.1087 19.3488 16.932 18.9712C16.9284 18.8925 16.9049 18.8139 16.8634 18.745C16.8526 18.7273 16.5875 18.3005 16.3674 17.974C17.4154 17.8816 17.8933 17.6574 17.9474 17.6298C17.9871 17.6102 18.0232 17.5846 18.0539 17.5531C18.5156 17.4135 19.1397 17.1912 19.1667 17.1814C19.2497 17.1519 19.32 17.0949 19.3687 17.0221C19.4589 16.9768 19.5401 16.9316 19.6086 16.8883C19.7457 16.8038 20 16.6484 20 16.3416C20 16.0347 19.7475 15.8794 19.6122 15.7948C19.5419 15.7515 19.4589 15.7043 19.3651 15.6571C19.3164 15.5863 19.2479 15.5312 19.1667 15.5037C19.1415 15.4939 18.5571 15.2873 18.1007 15.1457C18.0845 15.1398 18.0683 15.1359 18.052 15.13C18.0214 15.1005 17.9853 15.073 17.9474 15.0553C17.915 15.0395 17.6895 14.9333 17.2278 14.835C17.2368 14.8212 17.2458 14.8094 17.2566 14.7956C18.2541 13.5624 18.5192 12.8111 18.53 12.7796C18.5481 12.7265 18.5571 12.6715 18.5553 12.6164C18.8547 12.0401 19.2425 11.2081 19.2587 11.1708C19.302 11.0764 19.3128 10.9721 19.293 10.8738C19.3399 10.7341 19.3777 10.6083 19.4066 10.4981C19.4571 10.2995 19.5437 9.96708 19.3363 9.7409C19.1451 9.53242 18.8799 9.58945 18.6455 9.66026C18.5426 9.69173 18.4236 9.73303 18.2919 9.78614C18.2054 9.76647 18.1116 9.77827 18.0268 9.82547C17.9979 9.84121 17.3035 10.2267 16.7787 10.5453C16.7516 10.5611 16.7246 10.5768 16.6975 10.5925C16.6488 10.5925 16.5983 10.6004 16.5496 10.6201C16.5225 10.6319 15.9346 10.8777 14.9552 11.7707C14.9498 11.7176 14.9443 11.6645 14.9371 11.6113C14.8325 9.83137 14.5097 9.03678 14.4952 9.00334C14.4718 8.94827 14.4411 8.90107 14.4032 8.8637C14.3924 8.82436 14.3816 8.78699 14.3726 8.74765C14.194 8.06123 13.9307 7.17813 13.9181 7.14076C13.8874 7.03848 13.8261 6.95588 13.7467 6.90277C13.6818 6.74739 13.6187 6.61168 13.5591 6.49957C13.4329 6.25765 13.276 6 13 6C12.724 6 12.5671 6.25962 12.4427 6.50351C12.385 6.61365 12.3236 6.74739 12.2605 6.89687C12.1793 6.94998 12.1144 7.03455 12.0837 7.13879C12.0711 7.18206 11.7771 8.16154 11.6004 8.85976C11.5607 8.8991 11.5282 8.94827 11.5048 9.00334C11.4903 9.03678 11.1657 9.83531 11.0629 11.6231C11.0575 11.6723 11.052 11.7215 11.0466 11.7707C10.0673 10.8777 9.47745 10.6319 9.4504 10.6201C9.4017 10.6004 9.353 10.5906 9.3025 10.5925C9.27544 10.5768 9.24839 10.5611 9.22134 10.5453C8.69827 10.2267 8.00386 9.84121 7.9732 9.82547C7.88843 9.77827 7.79464 9.76647 7.70806 9.78614C7.5764 9.73303 7.45736 9.69173 7.35455 9.66026C7.12007 9.58945 6.85493 9.53242 6.66375 9.7409C6.45633 9.96708 6.5411 10.2995 6.5934 10.4981C6.62226 10.6083 6.66014 10.7341 6.70703 10.8738C6.68719 10.9702 6.69802 11.0764 6.7413 11.1708C6.75934 11.2081 7.14713 12.0401 7.44473 12.6164C7.44473 12.6715 7.45194 12.7265 7.46998 12.7796C7.4808 12.8111 7.74594 13.5624 8.74337 14.7956C8.75238 14.8094 8.7614 14.8212 8.77222 14.835C8.31049 14.9313 8.08503 15.0376 8.05256 15.0553C8.01288 15.0749 7.97861 15.1005 7.94795 15.13C7.93172 15.1359 7.91549 15.1398 7.89925 15.1457C7.44293 15.2873 6.85854 15.4958 6.83329 15.5037C6.75213 15.5332 6.68359 15.5883 6.63489 15.6571C6.5411 15.7043 6.45813 15.7515 6.38779 15.7948C6.25251 15.8794 6 16.0347 6 16.3416C6 16.6484 6.25432 16.8038 6.39139 16.8883C6.45993 16.9296 6.53929 16.9749 6.63128 17.0221C6.67998 17.0949 6.74852 17.1519 6.83329 17.1814C6.86215 17.1912 7.48622 17.4135 7.94615 17.5531C7.97681 17.5846 8.01288 17.6102 8.05256 17.6298C8.10667 17.6574 8.58464 17.8796 9.63257 17.974C9.41252 18.3005 9.14739 18.7273 9.13656 18.745C9.09328 18.8139 9.07163 18.8925 9.06802 18.9712C8.89126 19.3488 8.84437 19.6026 8.99588 19.813C9.1041 19.9644 9.2556 19.9999 9.38366 19.9999Z"/>
          <path d="M12.8203 5.01074C12.4172 5.06137 12.122 5.27642 11.9346 5.47656C11.7456 5.67842 11.6235 5.91021 11.5576 6.03906C11.5134 6.12343 11.4692 6.21417 11.4268 6.30762C11.3164 6.43259 11.228 6.57737 11.166 6.73535L11.124 6.85645V6.8584C11.124 6.8584 11.1233 6.86248 11.1201 6.87305C11.1173 6.88249 11.1132 6.89602 11.1084 6.91211C11.0987 6.9448 11.0846 6.98985 11.0684 7.04492C11.0358 7.15518 10.9917 7.30541 10.9424 7.47656C10.8608 7.75995 10.7654 8.10782 10.6816 8.42676C10.6448 8.48771 10.6116 8.5489 10.585 8.61133H10.5859C10.5406 8.71763 10.3842 9.13259 10.249 9.91113C10.1666 9.86254 10.0945 9.82334 10.0342 9.79199C9.98384 9.76584 9.94171 9.74508 9.9082 9.72949C9.87731 9.71513 9.8535 9.70437 9.85059 9.70312L9.83789 9.69824L9.8252 9.69238C9.76939 9.66984 9.70926 9.65181 9.64648 9.63574C9.12478 9.32183 8.4858 8.96434 8.42969 8.93555V8.93652C8.25437 8.84435 8.06528 8.79116 7.87402 8.7793C7.79414 8.75068 7.71839 8.72582 7.64746 8.7041L7.64355 8.70312C7.48511 8.65528 6.69261 8.38567 6.05273 8.94238L5.92676 9.06543L5.81348 9.2041C5.57758 9.53291 5.52789 9.88959 5.53125 10.1436C5.53496 10.4183 5.60205 10.6621 5.62598 10.7529C5.64672 10.832 5.67256 10.9145 5.69922 11.001C5.70354 11.2005 5.74638 11.4011 5.83203 11.5879L5.83594 11.5967L5.84082 11.6055C5.84149 11.6069 5.84629 11.6177 5.85938 11.6455C5.87233 11.673 5.89106 11.7111 5.91309 11.7578C5.95719 11.8513 6.01667 11.9794 6.08594 12.124C6.19878 12.3595 6.34129 12.6443 6.47363 12.9062C6.48567 12.9692 6.50085 13.0351 6.52344 13.1016L6.52441 13.1045C6.55851 13.2036 6.72477 13.6383 7.17285 14.332C7.06893 14.3667 6.96846 14.3983 6.87988 14.4287C6.77436 14.4649 6.68252 14.4973 6.61621 14.5205C6.53043 14.5505 6.52086 14.5536 6.53613 14.5488L6.51367 14.5557L6.49121 14.5635C6.30985 14.6294 6.15201 14.7299 6.02051 14.8506C6.01821 14.8519 6.01596 14.8532 6.01367 14.8545L5.86426 14.9434L5.85742 14.9473C5.78945 14.9895 5 15.414 5 16.3418C5.00014 17.2746 5.79876 17.6977 5.86621 17.7393L5.875 17.7451C5.9128 17.7679 5.9553 17.7887 5.99902 17.8135C6.13597 17.9452 6.30462 18.0563 6.50488 18.126L6.51074 18.1279C6.50816 18.127 6.51324 18.1286 6.53906 18.1377C6.55993 18.145 6.58871 18.1556 6.62402 18.168C6.69528 18.1929 6.79304 18.2266 6.9043 18.2646C7.07384 18.3226 7.28241 18.389 7.48145 18.4521C7.52047 18.4768 7.55976 18.4994 7.59961 18.5195L7.59863 18.5215C7.67803 18.5619 7.83928 18.6311 8.0918 18.707C7.9206 19.1147 7.73769 19.7714 8.18359 20.3936L8.18262 20.3945C8.54592 20.9027 9.06945 21 9.38379 21C9.53811 21 9.66778 20.9774 9.72852 20.9668V20.9678C9.75464 20.9633 9.78064 20.9572 9.80664 20.9521C9.97682 20.9566 10.1506 20.9286 10.3203 20.8652V20.8672C10.3602 20.8527 10.7353 20.7025 11.0723 20.5586C11.1002 20.5536 11.1291 20.5466 11.1592 20.5391V20.541C11.1645 20.5397 11.1699 20.5367 11.1758 20.5352C11.1836 20.5331 11.1923 20.5325 11.2002 20.5303V20.5283C11.4267 20.4658 11.9644 20.2638 12.7539 19.6885L12.8271 19.6396C12.8891 19.6157 12.9466 19.588 13 19.5586C13.0579 19.5907 13.117 19.6186 13.1777 19.6416C13.197 19.6544 13.2165 19.6676 13.2363 19.6807C14.0384 20.2697 14.585 20.4719 14.8145 20.5322L14.8135 20.5342C14.822 20.5365 14.8312 20.5369 14.8398 20.5391C14.8429 20.5398 14.8457 20.5423 14.8486 20.543L14.8496 20.542C14.8752 20.5482 14.9012 20.5538 14.9277 20.5586C15.2322 20.6886 15.5677 20.8232 15.6592 20.8584C15.6631 20.8599 15.6669 20.8618 15.6709 20.8633C15.6747 20.8647 15.6776 20.8664 15.6797 20.8672L15.6807 20.8662C15.8484 20.9283 16.0229 20.9559 16.1963 20.9512C16.2246 20.9564 16.2494 20.964 16.2715 20.9678L16.2754 20.9688C16.3353 20.9788 17.2563 21.1872 17.8203 20.3916V20.3906C18.2598 19.7709 18.0812 19.1177 17.9092 18.707C18.1617 18.6308 18.3227 18.5615 18.4014 18.5215V18.5195C18.4404 18.4998 18.4793 18.4782 18.5176 18.4541C18.9527 18.3164 19.4365 18.1465 19.502 18.123C19.6912 18.0557 19.8566 17.9511 19.9932 17.8223C20.0443 17.7936 20.0946 17.7647 20.1426 17.7344L20.1416 17.7334C20.2425 17.6742 20.906 17.2991 20.9912 16.5107L21 16.3418L20.9912 16.1729C20.904 15.362 20.2063 14.9869 20.1426 14.9473L20.1357 14.9434C20.0888 14.9145 20.0389 14.883 19.9844 14.8525C19.8483 14.7263 19.6817 14.6223 19.4883 14.5566V14.5576C19.3851 14.5205 19.1177 14.4274 18.8281 14.3311C19.2756 13.6379 19.4415 13.2036 19.4756 13.1045L19.4766 13.1016C19.4974 13.0404 19.5142 12.9758 19.5273 12.9092C19.8009 12.3704 20.1094 11.7133 20.167 11.5869C20.1694 11.5816 20.1724 11.5766 20.1748 11.5713L20.1758 11.5693L20.1748 11.5684C20.2554 11.386 20.2967 11.1933 20.3008 11.001C20.3275 10.9141 20.3532 10.8313 20.374 10.752L20.376 10.7441C20.3972 10.6609 20.4637 10.4163 20.4678 10.1445C20.472 9.85558 20.408 9.43064 20.0732 9.06543L19.9473 8.94238C19.478 8.53408 18.9267 8.57051 18.6035 8.6377L18.3564 8.70312L18.3525 8.7041C18.2819 8.72574 18.2065 8.75081 18.127 8.7793C17.9276 8.79152 17.7303 8.84785 17.5488 8.94727C17.5149 8.96574 16.8805 9.31891 16.3525 9.63574C16.3396 9.639 16.3265 9.64181 16.3135 9.64551L16.1748 9.69238L16.1621 9.69824L16.1494 9.70312C16.1469 9.70418 16.1228 9.71457 16.0908 9.72949C16.0573 9.74512 16.0151 9.76579 15.9648 9.79199C15.9048 9.82328 15.834 9.86367 15.752 9.91211C15.6167 9.13369 15.4607 8.71835 15.415 8.61133L15.3594 8.49512C15.3485 8.47453 15.3364 8.45419 15.3242 8.43359C15.1588 7.80432 14.9306 7.03049 14.876 6.85352C14.817 6.65699 14.7177 6.47339 14.582 6.31738C14.5361 6.21623 14.4898 6.11953 14.4424 6.03027C14.377 5.90519 14.2531 5.67324 14.0625 5.47168C13.8478 5.24461 13.4935 5 13 5L12.8203 5.01074Z" stroke="white" stroke-width="2"/>
        </g>
        <defs>
          <filter id="filter0_d_706_205" x="0" y="0" width="26" height="26" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset/>
            <feGaussianBlur stdDeviation="2"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.19 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_706_205"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_706_205" result="shape"/>
          </filter>
        </defs>
      </svg>
    `;

    // Label : uniquement le palier de réduction (pas le nombre de produits)
    const label = document.createElement("div");
    label.className = "progress-label";

    const labelDiscount = document.createElement("span");
    labelDiscount.className = "progress-label-discount";
    labelDiscount.textContent =
      this.discountUnit === "percentage"
        ? `-${tier.discount}%`
        : `-${tier.discount}€`;

    label.appendChild(labelDiscount);

    // Assembler
    iconWrapper.appendChild(iconDiv);
    iconWrapper.appendChild(label);
    progressGroup.appendChild(progressElement);
    progressGroup.appendChild(iconWrapper);

    return progressGroup;
  }

  /**
   * Met à jour la barre de progression vers les paliers
   */
  updateProgressBar(value, discount) {
    // Créer les labels depuis la config et trier par seuil croissant
    // Adapter les labels selon le type de palier (montant ou nombre de produits)
    const tiers = this.tiersConfig
      .map((tier) => ({
        threshold: tier.threshold,
        discount: tier.discount,
        label: this.tierType === "amount"
          ? `${tier.threshold}€`
          : `${tier.threshold} produit${tier.threshold > 1 ? 's' : ''}`,
      }))
      .sort((a, b) => a.threshold - b.threshold); // Trier par seuil croissant

    // Déterminer la valeur actuelle selon le type
    const currentValue = this.tierType === "amount"
      ? value / 100  // value est en centimes, convertir en euros
      : value;       // value est directement le nombre de produits

    if (tiers.length === 0) {
      if (this.progressContainer) {
        this.progressContainer.style.display = "none";
      }
      return;
    }

    if (currentValue === 0) {
      if (this.progressContainer) {
        this.progressContainer.style.display = "none";
      }
      return;
    }

    // Mettre à jour la barre de progression non-sticky (sidebar)
    if (this.progressContainer && this.progressBars) {
      this.progressContainer.style.display = "block";
      this.progressBars.innerHTML = "";

      for (let i = 0; i < tiers.length; i++) {
        const tier = tiers[i];
        if (!tier) continue;

        let progressValue = 0;
        let isReached = false;

        if (currentValue >= tier.threshold) {
          progressValue = 100;
          isReached = true;
        } else {
          if (i === 0) {
            progressValue = Math.min(
              100,
              (currentValue / tier.threshold) * 100
            );
          } else {
            const previousTier = tiers[i - 1];
            if (previousTier && currentValue >= previousTier.threshold) {
              const range = tier.threshold - previousTier.threshold;
              const progress = currentValue - previousTier.threshold;
              progressValue = Math.min(100, (progress / range) * 100);
            } else {
              progressValue = 0;
            }
          }
        }

        const progressGroup = this.createProgressBarElement(
          tier,
          progressValue,
          isReached,
          currentValue
        );
        this.progressBars.appendChild(progressGroup);
      }
    }

    // Mettre à jour le texte de progression
    let currentTier = null;
    let nextTier = null;

    for (let i = 0; i < tiers.length; i++) {
      if (currentValue >= tiers[i].threshold) {
        currentTier = tiers[i];
        if (i < tiers.length - 1) {
          nextTier = tiers[i + 1];
        }
      } else {
        if (!nextTier) {
          nextTier = tiers[i];
        }
        break;
      }
    }

    // Formater la valeur actuelle selon le type
    const currentValueText = this.tierType === "amount"
      ? this.formatMoney(value)
      : `${currentValue} produit${currentValue > 1 ? 's' : ''}`;

    let progressText = "";
    if (currentTier && !nextTier) {
      progressText = `${currentValueText} - Palier max atteint !`;
    } else if (currentTier && nextTier) {
      progressText = `Progression : ${currentValueText} / ${nextTier.label}`;
    } else if (nextTier) {
      progressText = `Progression : ${currentValueText} / ${nextTier.label}`;
    } else {
      const maxTier = tiers[tiers.length - 1];
      if (maxTier) {
        progressText = `Progression : ${currentValueText} / ${maxTier.label}`;
      }
    }

    if (this.progressAmount) {
      this.progressAmount.textContent = progressText;
    }

    // Mettre à jour le texte avec les étapes
    if (this.progressAmount) {
      let currentTier = null;
      let nextTier = null;

      for (let i = 0; i < tiers.length; i++) {
        if (currentValue >= tiers[i].threshold) {
          currentTier = tiers[i];
          if (i < tiers.length - 1) {
            nextTier = tiers[i + 1];
          }
        } else {
          if (!nextTier) {
            nextTier = tiers[i];
          }
          break;
        }
      }

      if (currentTier && !nextTier) {
        this.progressAmount.textContent = `${currentValueText} - Palier max atteint !`;
      } else if (currentTier && nextTier) {
        this.progressAmount.textContent = `${currentValueText} / ${nextTier.label}`;
      } else if (nextTier) {
        this.progressAmount.textContent = `${currentValueText} / ${nextTier.label}`;
      } else {
        const maxTier = tiers[tiers.length - 1];
        if (maxTier) {
          this.progressAmount.textContent = `${currentValueText} / ${maxTier.label}`;
        }
      }
    }
  }

  updateTiersMarkers(value, tiers) {
    const tierElements = this.section.querySelectorAll(".pack-products__tier");

    // Déterminer la valeur actuelle selon le type
    const currentValue = this.tierType === "amount"
      ? value / 100  // value est en centimes, convertir en euros
      : value;       // value est directement le nombre de produits

    tierElements.forEach((element) => {
      const tierThreshold = parseInt(element.dataset.tierThreshold) || 0;
      const tierIndex = tiers.findIndex((t) => t.threshold === tierThreshold);

      // Réinitialiser les classes
      element.classList.remove(
        "pack-products__tier--reached",
        "pack-products__tier--next"
      );

      if (currentValue >= tierThreshold) {
        // Palier atteint
        element.classList.add("pack-products__tier--reached");
      } else {
        // Vérifier si c'est le prochain palier
        const reachedTiers = tiers.filter((t) => currentValue >= t.threshold);
        if (reachedTiers.length === 0 && tierIndex === tiers.length - 1) {
          // Premier palier non atteint (le plus bas)
          element.classList.add("pack-products__tier--next");
        } else if (reachedTiers.length > 0) {
          // Trouver le palier le plus élevé atteint
          const highestReachedIndex = Math.max(
            ...reachedTiers.map((t) => tiers.indexOf(t))
          );
          if (tierIndex === highestReachedIndex + 1) {
            // C'est le prochain palier
            element.classList.add("pack-products__tier--next");
          }
        }
      }
    });
  }

  /**
   * Met à jour la sidebar (non sticky) avec les produits sélectionnés
   */
  updateSummary() {
    if (!this.summaryItems || !this.summaryTotal || !this.summaryCount) {
      return;
    }

    const selectedItems = this.getSelectedItems();
    const { subtotal, total, discount } = this.calculateTotalWithDiscount();

    const isTwoPlusOne = this.bundleMode === "two_plus_one_free";
    const count =
      isTwoPlusOne && this.freeGiftVariantId
        ? selectedItems.length + 1
        : selectedItems.length;

    this.summaryCount.textContent = `${count} produit${count > 1 ? "s" : ""}`;

    // Montant économisé au-dessus du total (affiché uniquement si discount > 0)
    if (this.summarySavings) {
      if (discount > 0) {
        let savingsText = `Vous économisez ${this.formatMoney(discount)}`;
        // En mode pourcentage, afficher l'offre en % (ex: tu économises 20%)
        if (this.discountUnit === "percentage" && !isTwoPlusOne) {
          const value = this.tierType === "amount" ? subtotal : this.getSelectedCount();
          const tier = this.getReachedTier(value);
          if (tier) {
            savingsText = `Bravo ! ${tier.discount}% d'économisé 🥳`;
          }
        }
        this.summarySavings.textContent = `${savingsText} 🎉`;
        this.summarySavings.style.display = "";
      } else {
        this.summarySavings.textContent = "";
        this.summarySavings.style.display = "none";
      }
    }

    // Mettre à jour le total (le montant économisé est affiché au-dessus)
    this.summaryTotal.textContent = this.formatMoney(total);

    // Mettre à jour la liste des produits
    this.summaryItems.innerHTML = "";
    selectedItems.forEach((item) => {
      this.summaryItems.appendChild(this.createStickyItem(item));
    });
    if (isTwoPlusOne && this.freeGiftVariantId) {
      const freeOpt = this.freeGiftConfig.find(
        (o) => String(o.id) === String(this.freeGiftVariantId)
      );
      if (freeOpt) {
        this.summaryItems.appendChild(
          this.createFreeGiftStickyItem(freeOpt)
        );
      }
    }
  }

  /**
   * Crée un élément pour le cadeau offert (mode 2+1)
   */
  createFreeGiftStickyItem(freeOpt) {
    const el = document.createElement("div");
    el.className = "pack-products__sticky-item pack-products__sticky-item--free";
    el.setAttribute("role", "listitem");
    const imgHtml = freeOpt.image_url
      ? `<img src="${freeOpt.image_url}" alt="${(freeOpt.title || "").replace(/"/g, "&quot;")}" loading="lazy">`
      : "";
    el.innerHTML = `
      <div class="pack-products__sticky-item-image">${imgHtml}</div>
      <div class="pack-products__sticky-item-info">
        <div class="pack-products__sticky-item-title">${freeOpt.title || ""} ${(freeOpt.variant_title || "").replace(/^[- ]+/, "")}</div>
        <div class="pack-products__sticky-item-variant">Offert</div>
        <div class="pack-products__sticky-item-price" style="color: #22c55e;">Gratuit</div>
      </div>
    `;
    return el;
  }

  /**
   * Crée un élément pour un produit sélectionné (utilisé dans la sidebar)
   */
  createStickyItem(item) {
    const product = this.productsData[item.productKey];
    if (!product) return document.createElement("div");

    const itemElement = document.createElement("div");
    itemElement.className = "pack-products__sticky-item";
    itemElement.setAttribute("role", "listitem");
    itemElement.setAttribute("data-product-key", item.productKey);

    // Trouver le titre de la variante
    const variant = product.variants.find(
      (v) => v.id.toString() === item.variantId.toString()
    );
    const variantTitle = variant ? variant.title : "";

    itemElement.innerHTML = `
      <div class="pack-products__sticky-item-image">
        ${
          product.featured_image
            ? `<img src="${product.featured_image.url}" alt="${product.title}" loading="lazy">`
            : ""
        }
      </div>
      <div class="pack-products__sticky-item-info">
        <div class="pack-products__sticky-item-title">${product.title}</div>
        ${
          variantTitle
            ? `<div class="pack-products__sticky-item-variant">${variantTitle}</div>`
            : ""
        }
        <div class="pack-products__sticky-item-price">
          ${
            item.compareAtPrice && item.compareAtPrice > item.price
              ? `<s class="pack-products__sticky-item-compare">${this.formatMoney(item.compareAtPrice)}</s> `
              : ""
          }${this.formatMoney(item.price)}
        </div>
      </div>
      <button
        type="button"
        class="pack-products__sticky-item-remove"
        aria-label="Retirer ${product.title} du pack"
        data-product-key="${item.productKey}"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    // Ajouter l'événement de suppression
    const removeButton = itemElement.querySelector(
      ".pack-products__sticky-item-remove"
    );
    if (removeButton) {
      removeButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeFromSticky(item.productKey);
      });
    }

    return itemElement;
  }

  /**
   * Retire un produit de la sélection sticky
   */
  removeFromSticky(productKey) {
    const state = this.selectionState.get(productKey);
    if (state) {
      state.checked = false;
      this.selectionState.set(productKey, state);

      // Mettre à jour l'affichage de la carte
      this.updateCardSelection(productKey, false);

      // Mettre à jour le total et l'affichage
      this.updateDisplay();

      // Annoncer pour les screen readers
      this.announceSelection(productKey, false);
    }
  }

  /**
   * Calcule le total des produits sélectionnés
   */
  calculateTotal() {
    let total = 0;
    this.selectionState.forEach((state) => {
      if (state.checked && state.price) {
        total += state.price;
      }
    });
    return total;
  }

  /**
   * Charge la configuration des paliers depuis les éléments DOM
   */
  loadTiersConfig() {
    const tiers = [];
    const tierElements = this.section.querySelectorAll(".pack-products__tier");

    tierElements.forEach((element) => {
      const threshold = parseInt(element.dataset.tierThreshold) || 0;
      const discount = parseInt(element.dataset.tierDiscount) || 0;
      const code = (element.dataset.tierCode || "").trim();
      if (threshold > 0 && discount > 0) {
        tiers.push({ threshold, discount, code });
      }
    });

    // Aucun fallback : seuls les paliers explicitement configurés sont affichés.
    // Trier par seuil croissant (du plus bas au plus élevé)
    tiers.sort((a, b) => a.threshold - b.threshold);

    return tiers;
  }

  /**
   * Retourne le palier atteint le plus élevé (ou null).
   * Les paliers ne se cumulent pas : on prend le plus haut palier atteint.
   * @param {number} value - Sous-total (centimes) si tierType "amount", sinon nombre de produits
   * @returns {{threshold: number, discount: number, code?: string}|null} - Le palier atteint ou null
   */
  getReachedTier(value) {
    const sortedTiers = [...this.tiersConfig].sort(
      (a, b) => b.threshold - a.threshold
    );
    // Les seuils "amount" sont en euros : on convertit la valeur (centimes -> euros)
    const compareValue = this.tierType === "amount" ? value / 100 : value;
    for (const tier of sortedTiers) {
      if (compareValue >= tier.threshold) {
        return tier;
      }
    }
    return null;
  }

  /**
   * Calcule la réduction applicable selon les paliers configurés
   * @param {number} value - Sous-total (centimes) si tierType "amount", sinon nombre de produits
   * @param {number} subtotal - Sous-total du pack en centimes (base du % le cas échéant)
   * @returns {number} - Réduction en centimes
   */
  calculateDiscount(value, subtotal) {
    const tier = this.getReachedTier(value);
    if (!tier) return 0;
    if (this.discountUnit === "percentage") {
      // Pourcentage du sous-total du pack
      return Math.round((subtotal * tier.discount) / 100);
    }
    // Montant fixe en euros -> centimes
    return tier.discount * 100;
  }

  /**
   * Calcule le total avec réduction
   */
  calculateTotalWithDiscount() {
    const subtotal = this.calculateTotal();
    const productCount = this.getSelectedCount();

    if (this.bundleMode === "two_plus_one_free") {
      // Total = somme des 2 payants, discount = valeur du cadeau offert (affichage)
      const freeGiftValue = this.freeGiftVariantId
        ? (() => {
            const opt = this.freeGiftConfig.find(
              (o) => String(o.id) === String(this.freeGiftVariantId)
            );
            return opt ? (opt.price || 0) : 0;
          })()
        : 0;
      return {
        subtotal,
        discount: freeGiftValue,
        total: subtotal,
      };
    }

    const value = this.tierType === "amount" ? subtotal : productCount;
    const discount = this.calculateDiscount(value, subtotal);

    return {
      subtotal,
      discount,
      total: Math.max(0, subtotal - discount),
    };
  }

  /**
   * Retourne le code promo à appliquer selon le montant ou le nombre de produits
   * Mapping basé sur le montant de la réduction (en euros)
   * - 60  -> BUNDLE60
   * - 40  -> BUNDLE40
   * - 30  -> BUNDLE30
   * - 15  -> BUNDLE15
   * @param {number} value - Montant en centimes OU nombre de produits selon tierType
   */
  getDiscountCodeForSubtotal(value) {
    const tier = this.getReachedTier(value);

    if (!tier) {
      console.log("[Pack Products] Discount Code Calculation:", {
        tierType: this.tierType,
        value: this.tierType === "amount" ? `${value / 100}€` : `${value} produits`,
        discountCode: "NONE (no tier reached)",
      });
      return "";
    }

    // Priorité au code configuré par palier ; sinon, mapping BUNDLE historique (€)
    let code = tier.code || "";
    if (!code && this.discountUnit !== "percentage") {
      switch (tier.discount) {
        case 60:
          code = "BUNDLE60";
          break;
        case 40:
          code = "BUNDLE40";
          break;
        case 30:
          code = "BUNDLE30";
          break;
        case 15:
          code = "BUNDLE15";
          break;
        default:
          code = "";
      }
    }

    console.log("[Pack Products] Discount Code Calculation:", {
      tierType: this.tierType,
      discountUnit: this.discountUnit,
      value: this.tierType === "amount" ? `${value / 100}€` : `${value} produits`,
      tierThreshold: this.tierType === "amount" ? `${tier.threshold}€` : `${tier.threshold} produits`,
      discount: this.discountUnit === "percentage" ? `${tier.discount}%` : `${tier.discount}€`,
      discountCode: code || "NONE",
    });
    return code;
  }

  /**
   * Compte le nombre de produits sélectionnés
   */
  getSelectedCount() {
    let count = 0;
    this.selectionState.forEach((state) => {
      if (state.checked) count++;
    });
    return count;
  }

  /**
   * Met à jour le message de statut pour les screen readers
   */
  updateStatusMessage(selectedCount, total, discount = 0) {
    if (this.statusDisplay) {
      let message;
      if (selectedCount === 0) {
        message = "Sélectionne tes produits pour bénéficier de l'offre ⚡️";
      } else if (selectedCount === 1) {
        if (discount > 0) {
          message = `1 produit sélectionné, total : ${this.formatMoney(
            total
          )} (réduction de ${this.formatMoney(discount)} appliquée)`;
        } else {
        message = `1 produit sélectionné, total : ${this.formatMoney(total)}`;
        }
      } else {1
        if (discount > 0) {
          message = `${selectedCount} produits sélectionnés, total : ${this.formatMoney(
            total
          )} (réduction de ${this.formatMoney(discount)} appliquée)`;
        } else {
          message = `${selectedCount} produits sélectionnés, total : ${this.formatMoney(
            total
          )}`;
        }
      }
      this.statusDisplay.textContent = message;
    }
  }

  /**
   * Annonce la sélection/désélection pour les screen readers
   */
  announceSelection(productKey, isSelected) {
    const product = this.productsData[productKey];
    if (product && this.statusDisplay) {
      const action = isSelected ? "ajouté à" : "retiré de";
      const tempMessage = `${product.title} ${action} la sélection`;

      // Annonce temporaire
      this.statusDisplay.textContent = tempMessage;

      // Retour au message de statut après un délai
      setTimeout(() => {
        const { total, discount } = this.calculateTotalWithDiscount();
        this.updateStatusMessage(this.getSelectedCount(), total, discount);
      }, 1000);
    }
  }

  /**
   * Récupère les IDs des sections cart-items pour le bundled section rendering
   */
  getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    return Array.from(cartItemsComponents)
      .filter((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .map((el) => el.dataset.sectionId);
  }

  /**
   * Gère l'ajout au panier groupé avec application du code promo bundle
   */
  async handleAddToCart() {
    const selectedItems = this.getSelectedItems();
    const isTwoPlusOne = this.bundleMode === "two_plus_one_free";

    if (selectedItems.length === 0) {
      return;
    }
    if (isTwoPlusOne && (!this.freeGiftVariantId || selectedItems.length !== 2)) {
      return;
    }

    const { subtotal, discount } = this.calculateTotalWithDiscount();

    this.animateAddToCart();
    this.animateFlyToCart();
    this.setLoadingState(true);

    try {
      let items = selectedItems.map((item) => ({
        id: item.variantId,
        quantity: 1,
      }));

      if (isTwoPlusOne && this.freeGiftVariantId) {
        items.push({ id: this.freeGiftVariantId, quantity: 1 });
      }

      const response = await fetch(this.cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      await response.json();

      const sectionIds = this.getCartSectionIds();
      const sections =
        (await this.applyBundleDiscount(subtotal, {
          skipVerification: true,
          sectionIds,
        })) || {};

      const itemCount = isTwoPlusOne ? 3 : selectedItems.length;
      this.handleAddToCartSuccess(itemCount, discount);

      document.dispatchEvent(
        new CartAddEvent({}, this.sectionId, {
          source: "pack-products-section",
          itemCount,
          productId: selectedItems[0]?.productId || null,
          sections,
          discount,
        })
      );
    } catch (error) {
      console.error("Erreur lors de l'ajout au panier:", error); 
      this.handleAddToCartError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Récupère les IDs des produits bundle depuis les data-attributes
   */
  getBundleProductIds() {
    const bundleIdsAttr = this.section.dataset.bundleProductIds;
    if (bundleIdsAttr) {
      return bundleIdsAttr
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));
    }
    return [];
  }

  /**
   * Vérifie qu'il y a au minimum un certain nombre de produits bundle dans le panier
   * @param {number} minCount - Nombre minimum requis (défaut: 2)
   * @returns {Promise<boolean>}
   */
  async verifyMinimumBundleItems(minCount = 2) {
    try {
      let cart;
      if (typeof window.ThemeCartSync?.getCart === "function") {
        cart = await window.ThemeCartSync.getCart();
      } else {
        const cartResponse = await fetch("/cart.js");
        if (!cartResponse.ok) {
          console.warn("[Pack Products] Impossible de récupérer le panier");
          return false;
        }
        cart = await cartResponse.json();
      }

      const bundleProductIds = this.getBundleProductIds();

      if (bundleProductIds.length === 0) {
        console.warn(
          "[Pack Products] No bundle product IDs configured. Add data-bundle-product-ids attribute to the section."
        );
        return false;
      }

      const bundleItemsCount = cart.items.filter((item) =>
        bundleProductIds.includes(item.product_id)
      ).length;
      return bundleItemsCount >= minCount;
    } catch (error) {
      console.warn("[Pack Products] Error verifying bundle items:", error);
      return false;
    }
  }

  /**
   * Retire le code de réduction bundle du panier
   */
  async removeBundleDiscount() {
    try {
      const response = await fetch(this.cartUpdateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: "",
        }),
      });

      if (response.ok) {
        return true;
      } else {
        console.warn("[Pack Products] Impossible de retirer le code bundle", {
          status: response.status,
        });
        return false;
      }
    } catch (error) {
      console.warn(
        "[Pack Products] Erreur lors de la suppression du code:",
        error
      );
      return false;
    }
  }

  /**
   * Applique le code promo bundle sur le panier via /cart/update.js
   * Utilise le bundled section rendering pour récupérer le HTML des sections en une seule requête.
   *
   * @param {number} subtotal - Sous-total du pack
   * @param {Object} [options] - Options
   * @param {boolean} [options.skipVerification] - Skip la vérification cart.js (quand on vient d'ajouter)
   * @param {string[]} [options.sectionIds] - IDs des sections à rendre (bundled section rendering)
   * @returns {Promise<Record<string, string>|undefined>} Sections HTML ou undefined
   */
  async applyBundleDiscount(subtotal, options = {}) {
    const { skipVerification = false, sectionIds = [] } = options;

    if (!skipVerification) {
      const hasMinimumBundleItems = await this.verifyMinimumBundleItems(2);
      if (!hasMinimumBundleItems) {
        await this.removeBundleDiscount();
        return undefined;
      }
    }

    let code = "";
    if (this.bundleMode === "two_plus_one_free") {
      code = this.freeGiftDiscountCode || "";
    } else {
      const productCount = this.getSelectedCount();
      const value = this.tierType === "amount" ? subtotal : productCount;
      code = this.getDiscountCodeForSubtotal(value);
    }

    const body = {
      discount: code || "",
      attributes: {
        _bundle_subtotal: subtotal.toString(),
        _bundle_code: code,
      },
    };

    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(",");
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(this.cartUpdateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (!response.ok) {
        return result?.sections || {};
      }

      return result?.sections;
    } catch {
      return {};
    }
  }

  /**
   * Récupère les items sélectionnés pour l'ajout au panier
   */
  getSelectedItems() {
    const items = [];
    this.selectionState.forEach((state, productKey) => {
      if (state.checked && state.selectedVariantId) {
        const product = this.productsData[productKey];
        items.push({
          productKey,
          variantId: state.selectedVariantId,
          price: state.price,
          compareAtPrice: state.compareAtPrice || 0,
          title: product.title,
          productId: product.id,
          featuredImage: product.featured_image,
        });
      }
    });
      return items;
    }

    /**
     * Ouvre le cart-drawer après ajout au panier
     */
    openCartDrawer() {
      // Méthode 1: Chercher le cart-drawer component et l'ouvrir
    const cartDrawer = document.querySelector("cart-drawer-component");
    if (cartDrawer && typeof cartDrawer.open === "function") {
        cartDrawer.open();
        return;
      }

      // Méthode 2: Event personnalisé pour déclencher l'ouverture
    document.dispatchEvent(new CustomEvent("cart:open-drawer"));

      // Méthode 3: Tentative de clic sur le bouton cart drawer
    const cartButton = document.querySelector(
      "[data-cart-drawer], .cart-drawer-toggle, .js-cart-link"
    );
      if (cartButton) {
        cartButton.click();
      } else {
        // Méthode 4: Chercher un drawer déjà présent et l'ouvrir
      const cartDrawerDialog = document.querySelector(
        '.cart-drawer, #cart-drawer, [role="dialog"][aria-label*="Cart"]'
      );
        if (cartDrawerDialog) {
        cartDrawerDialog.classList.add("is-open", "active");
        cartDrawerDialog.removeAttribute("hidden");
        if (
          cartDrawerDialog.showModal &&
          typeof cartDrawerDialog.showModal === "function"
        ) {
            cartDrawerDialog.showModal();
          }
        }
      }
    }

    /**
     * Anime le bouton d'ajout au panier (similaire au système standard)
     */
    animateAddToCart() {
      if (!this.addToCartButton) return;

      if (this.animationTimeout) clearTimeout(this.animationTimeout);
      if (this.cleanupTimeout) clearTimeout(this.cleanupTimeout);

    if (
      !this.addToCartButton.classList.contains(
        "pack-products__add-to-cart--added"
      )
    ) {
      this.addToCartButton.classList.add("pack-products__add-to-cart--added");
      }

      // Durée similaire au système standard (2000ms)
      this.animationTimeout = setTimeout(() => {
        this.cleanupTimeout = setTimeout(() => {
          if (this.addToCartButton) {
          this.addToCartButton.classList.remove(
            "pack-products__add-to-cart--added"
          );
          }
        }, 10);
      }, 2000);
    }

    /**
     * Anime l'effet "fly to cart" depuis le bouton vers l'icône du panier
     */
    animateFlyToCart() {
    const cartIcon = document.querySelector(
      ".header-actions__cart-icon, .cart-icon, .icon-cart"
    );

      if (!cartIcon || !this.addToCartButton) return;

      // Créer l'élément d'animation si fly-to-cart existe
    const flyToCartElement = document.createElement("fly-to-cart");

    if (!customElements.get("fly-to-cart")) {
        // Si fly-to-cart n'existe pas, créer une animation simple
        this.createSimpleFlyAnimation(cartIcon);
        return;
      }

      // Utiliser une image représentative des produits sélectionnés
      const selectedItems = this.getSelectedItems();
      if (selectedItems.length > 0 && selectedItems[0].featuredImage) {
      flyToCartElement.style.setProperty(
        "background-image",
        `url(${selectedItems[0].featuredImage.url})`
      );
      }

      flyToCartElement.source = this.addToCartButton;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
    }

    /**
     * Crée une animation simple si fly-to-cart n'est pas disponible
     */
    createSimpleFlyAnimation(cartIcon) {
      const buttonRect = this.addToCartButton.getBoundingClientRect();
      const cartRect = cartIcon.getBoundingClientRect();

      // Créer un élément animé simple
    const flyElement = document.createElement("div");
    flyElement.className = "pack-products__fly-animation";
      flyElement.style.cssText = `
        position: fixed;
        width: 20px;
        height: 20px;
        background: var(--color-primary-button-background, #ffe235);
        border-radius: 50%;
        z-index: 9999;
        pointer-events: none;
        left: ${buttonRect.left + buttonRect.width / 2}px;
        top: ${buttonRect.top + buttonRect.height / 2}px;
        transform: translate(-50%, -50%);
        transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      `;

      document.body.appendChild(flyElement);

      // Animer vers l'icône du panier
      requestAnimationFrame(() => {
        flyElement.style.left = `${cartRect.left + cartRect.width / 2}px`;
        flyElement.style.top = `${cartRect.top + cartRect.height / 2}px`;
      flyElement.style.opacity = "0";
      flyElement.style.transform = "translate(-50%, -50%) scale(0.3)";
      });

      // Nettoyer après l'animation
      setTimeout(() => {
        if (flyElement.parentNode) {
          flyElement.parentNode.removeChild(flyElement);
        }
      }, 600);
    }

    /**
     * Gère le succès de l'ajout au panier
     */
  handleAddToCartSuccess(itemCount, discount = 0) {
    // Réinitialiser toutes les sélections
    this.section
      .querySelectorAll('.pack-products__card[role="checkbox"]')
      .forEach((card) => {
        const productKey = card.dataset.productKey;
        this.updateCardSelection(productKey, false);
        const state = this.selectionState.get(productKey);
        if (state) {
          state.checked = false;
          this.selectionState.set(productKey, state);
        }
      });

    // Mode 2+1 : réinitialiser le cadeau offert
    this.freeGiftVariantId = null;
    this.updateFreeGiftCardsSelection();

    this.updateDisplay();

    // Message de succès
    if (this.statusDisplay) {
      let message;
      if (itemCount === 1) {
        message =
          discount > 0
            ? `1 produit ajouté au panier - ${this.formatMoney(discount)}`
            : "1 produit ajouté au panier";
      } else {
        message =
          discount > 0
            ? `${itemCount} produits ajoutés au panier - ${this.formatMoney(
                discount
              )}`
        : `${itemCount} produits ajoutés au panier`;
      }
      this.statusDisplay.textContent = message;

      // Retour au message normal après un délai
      setTimeout(() => {
        this.updateStatusMessage(0, 0, 0);
      }, 3000);
    }

    // Laisser le cart-drawer s'ouvrir via CartAddEvent (comme le product-form standard)
  }

  /**
   * Gère les erreurs d'ajout au panier
   */
  handleAddToCartError(error) {
    if (this.statusDisplay) {
      this.statusDisplay.textContent =
        "Erreur lors de l'ajout au panier. Veuillez réessayer.";

      setTimeout(() => {
        const { total, discount } = this.calculateTotalWithDiscount();
        this.updateStatusMessage(this.getSelectedCount(), total, discount);
      }, 3000);
    }
  }

  /**
   * Gère l'état de chargement du bouton
   */
  setLoadingState(isLoading) {
    // Bouton principal
    if (this.addToCartButton) {
      this.addToCartButton.disabled = isLoading;
      this.addToCartButton.classList.toggle(
        "pack-products__add-to-cart--loading",
        isLoading
      );

      const buttonText = this.addToCartButton.querySelector(
        ".pack-products__button-text"
      );
      if (buttonText) {
        buttonText.textContent = isLoading
          ? "Ajout en cours..."
          : this.section.querySelector("[data-button-label]")?.dataset
              .buttonLabel || "Ajouter au panier";
      }
    }

  }

  /**
   * Formate un prix selon le format de la boutique
   */
  formatMoney(cents) {
    if (typeof cents !== "number") {
      cents = parseInt(cents) || 0;
    }

    // Conversion centimes vers euros
    const amount = (cents / 100).toFixed(2);

    // Utilisation du format de la boutique ou fallback
    if (this.moneyFormat.includes("{{amount}}")) {
      return this.moneyFormat.replace("{{amount}}", amount);
    } else if (this.moneyFormat.includes("{{ amount }}")) {
      return this.moneyFormat.replace("{{ amount }}", amount);
    } else {
      // Fallback avec formatage français
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(cents / 100);
    }
  }
}

/**
 * Initialisation automatique
 */
document.addEventListener("DOMContentLoaded", () => {
  const packProductsSections = document.querySelectorAll(".pack-products");
  packProductsSections.forEach((section) => {
    new PackProducts(section);
  });
});

/**
 * Réinitialisation après mise à jour de section (Shopify admin)
 */
document.addEventListener("shopify:section:load", (event) => {
  if (
    event.detail.sectionId &&
    event.target.classList.contains("pack-products")
  ) {
    new PackProducts(event.target);
  }
});

// Export pour compatibilité avec le système de modules
export { PackProducts };
